// Progress model (§10.1) - derived purely from the session's REALIZED step path and the
// current step. No client-side step bookkeeping: the backend truncates `steps` after a
// short-circuit, so the rail always renders a true path.

// Player-facing phases (critique 3.6 - four phases, never "step 3 of 17").
export const PHASE_META = {
  harmful: { polarity: "negative", icon: "↓" }, // down arrow
  helpful: { polarity: "positive", icon: "↑" }, // up arrow
  observation: { polarity: null, icon: "◈" }, // diamond
  analysis: { polarity: null, icon: "⌕" }, // magnifier
};

export const PHASE_ORDER = ["harmful", "helpful", "observation", "analysis"];

// Map a step name to its phase (mirrors the GM monitor's phaseForStep). `complete` and
// unknown steps return null - they are not rendered as a rail phase.
export const phaseForStep = (step) => {
  if (!step) return null;
  if (step.startsWith("negative_")) return "harmful";
  if (step.startsWith("positive_")) return "helpful";
  if (step === "analysis_observation") return "observation";
  if (step.startsWith("analysis_")) return "analysis";
  return null;
};

const STEP_RE = /^(negative|positive)_(pair_1|pair_2|final)_(generality|outcome)$/;

// Decompose a comparison step name into its polarity / slot / question parts.
export const parseComparisonStep = (step) => {
  const match = STEP_RE.exec(step || "");
  if (!match) return null;
  return { polarity: match[1], slot: match[2], question: match[3] };
};

// Group the realized (playable) steps into ordered phases, each carrying per-step and
// per-phase done/current/upcoming states.
export const buildPhaseModel = (steps = [], currentStep = null) => {
  const playable = steps.filter((step) => step !== "complete");
  const currentIndex = playable.indexOf(currentStep);
  const allDone = currentIndex === -1; // current step advanced past the playable path
  const groups = [];

  playable.forEach((step, index) => {
    const phase = phaseForStep(step);
    if (!phase) return;

    let group = groups.find((candidate) => candidate.key === phase);
    if (!group) {
      group = { key: phase, meta: PHASE_META[phase], steps: [] };
      groups.push(group);
    }

    let state = "upcoming";
    if (allDone || index < currentIndex) {
      state = "done";
    } else if (index === currentIndex) {
      state = "current";
    }
    group.steps.push({ name: step, state });
  });

  return groups.map((group) => {
    let state = "upcoming";
    if (group.steps.some((step) => step.state === "current")) {
      state = "current";
    } else if (group.steps.every((step) => step.state === "done")) {
      state = "done";
    }
    return { ...group, state };
  });
};

// Analysis phase and past the observation floor -> backtracking is allowed (§6.3).
export const canStepBack = (step) => (
  typeof step === "string" && step.startsWith("analysis_") && step !== "analysis_observation"
);
